// BUILD-046 section A (Owner direction of 2026-10-21): which of Edit task, Hand off and History a task row offers, and the menu entries for them.
//
// ONE module for every place a task row is drawn with actions: Assigned by Me (Single and queue requests), Company / Team, and the recipients of an Everyone request. The same task therefore offers
// the same entries from either view. It mirrors the SERVER rules so the screen does not offer what the server will refuse; the server stays authoritative and every refusal is shown with its words:
//   Edit task  PUT /work/{id}: CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): the REQUESTER of an open task alone - its title, description, priority, job and collaborators - never a
//              source-governed or restricted task. Being the assignee or a collaborator grants none of it; the person doing the task records progress with Update. There is no admin override. The server judges
//              every request; this mirror only decides what is drawn.
//   Hand off   POST /work/{id}/handoff: the current assignee only, an open task, never an Everyone assignment, never a Source_Governed task. It COMPLETES the task and creates another.
//   History    GET /work/{id}/history: read only; the server decides who may read it (a participant including the requester, or an overseer). A row shown in its minimal form offers none.
// Reassign, Assign to, Return to queue and Reopen are NOT here: they are oversightActionsModel.offeredControls (TaskActionControls), unchanged.
//
// Pure module: no React, no I/O. Type-only import of MenuEntry. Erasable TypeScript only.
import type { MenuEntry } from "./RowMoreMenu";

export interface OfferMe {
  employee?: { employeeId?: string } | null;
}

export interface OfferRow {
  status?: string;
  assignmentKind?: string;
  ownerEmployeeId?: string;
  /** Owner decision D8: who issued the task. */
  requesterEmployeeId?: string;
  sourceGoverned?: boolean;
  redacted?: boolean;
  lifecycle?: string;
}

/** The two entries that open the host form or dialog (History opens in the row itself). */
export type TaskEntryKind = "edit" | "handoff" | "update";

export interface TaskEntryOffers {
  /** BUILD-046 section C: the Update form (status, progress note, next action and dates in one save). The same right as Edit task: the owner of an open task. */
  update: boolean;
  edit: boolean;
  handoff: boolean;
  history: boolean;
}

const OPEN_STATUSES: readonly string[] = ["active", "in_progress", "waiting", "blocked"];
const NONE: TaskEntryOffers = Object.freeze({ update: false, edit: false, handoff: false, history: false });

/** What one task row offers the viewer. Nothing for a viewer the summary does not name. */
export function taskEntryOffers(me: OfferMe | null | undefined, row: OfferRow | null | undefined): TaskEntryOffers {
  const viewer = typeof me?.employee?.employeeId === "string" ? me.employee.employeeId : "";
  if (viewer === "" || row === null || row === undefined) return NONE;
  // A row in its minimal form (restricted source) has nothing to edit, hand off or read.
  if (row.redacted === true) return NONE;
  if (row.lifecycle === "cancelled") return { update: false, edit: false, handoff: false, history: true };
  const owner = typeof row.ownerEmployeeId === "string" ? row.ownerEmployeeId : "";
  const open = typeof row.status === "string" && OPEN_STATUSES.includes(row.status);
  const isOwner = owner !== "" && owner === viewer;
  const kind = typeof row.assignmentKind === "string" ? row.assignmentKind : "";
  // Owner decision D8: the person who ISSUED an open task may edit its definition; a source-governed task is the originating module's.
  const isRequester = typeof row.requesterEmployeeId === "string" && row.requesterEmployeeId !== "" && row.requesterEmployeeId === viewer;
  const requesterMayEdit = isRequester && open && row.sourceGoverned !== true;
  return {
    update: isOwner && open,
    // CHANGED 2026-10-22 (BUILD-046 G1): the requester alone. The owner of an open task no longer edits its definition (the server refuses it); the owner's own entry is Update.
    edit: requesterMayEdit,
    handoff: isOwner && open && kind !== "everyone" && row.sourceGoverned !== true,
    history: true,
  };
}

export interface TaskEntryHandlers {
  /** Optional: a host that has no Update form simply does not offer the entry. */
  onUpdate?: () => void;
  onEdit: () => void;
  onHandoff: () => void;
  onHistory: () => void;
}

export const UPDATE_LABEL = "Update";
export const EDIT_LABEL = "Edit task";
export const HANDOFF_LABEL = "Hand off\u2026";
export const HISTORY_LABEL = "History";

/**
 * CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): the server lets only the person who ISSUED a task edit its title, description, priority, job and collaborators (authz.ts canUpdate), and refuses
 * the person doing it, a collaborator, a manager and everyone else with 403. A viewer who may not is shown Edit task as an unavailable entry with the reason, not silently without it. This is a wording of the server's
 * rule; it grants nothing. Null when there is nothing to explain: the viewer may edit it (or the entry does not apply: a minimal row, a closed or cancelled task).
 */
export const EDIT_NOT_OWNER_HINT = "Only the person who issued this task can edit it.";
export const EDIT_UNCLAIMED_HINT = "Only the person who issued this task can edit it. Whoever takes it records progress with Update.";
/** The person doing the task, who did not issue it: told what is theirs instead. */
// CHANGED 2026-10-25 (Builder request 2026-10-24, customer association): the requester also edits the task's customer, so the sentence names it ("... priority, job, customer and collaborators"). It is the same
// requester-only rule worded; it grants nothing.
export const EDIT_ASSIGNEE_HINT = "Only the person who issued this task can edit its title, description, priority, job, customer and collaborators. Use Update to record your progress.";
export const EDIT_GOVERNED_HINT = "This task's details belong to the originating module, so they cannot be edited here.";
export const EDIT_EVERYONE_HINT = "Not available for a whole Everyone request yet. Open its recipients and use Edit task on one person's task.";
/**
 * The reason Edit task is shown as unavailable to this viewer, or null when there is nothing to explain (the viewer may edit it, or the entry does not apply: a minimal row, a closed or cancelled task).
 * After Owner decision D8 the requester of an open task is offered the real entry, so the reasons left are: a source-governed task, and a viewer who neither does nor issued the task.
 */
export function editBlockedHint(me: OfferMe | null | undefined, row: OfferRow | null | undefined): string | null {
  const viewer = typeof me?.employee?.employeeId === "string" ? me.employee.employeeId : "";
  if (viewer === "" || row === null || row === undefined || row.redacted === true || row.lifecycle === "cancelled") return null;
  if (!(typeof row.status === "string" && OPEN_STATUSES.includes(row.status))) return null;
  const owner = typeof row.ownerEmployeeId === "string" ? row.ownerEmployeeId : "";
  const requester = typeof row.requesterEmployeeId === "string" ? row.requesterEmployeeId : "";
  if (requester === viewer) return row.sourceGoverned === true ? EDIT_GOVERNED_HINT : null;
  if (owner === viewer) return EDIT_ASSIGNEE_HINT;
  return owner === "" ? EDIT_UNCLAIMED_HINT : EDIT_NOT_OWNER_HINT;
}

/** The menu entries for the offers, in the order Edit task, Hand off, History (the order My Tasks uses). `editHint` (from editBlockedHint) adds Edit task as an unavailable entry that says why. */
export function taskEntries(offers: TaskEntryOffers, title: string, handlers: TaskEntryHandlers, options?: { editHint?: string | null }): MenuEntry[] {
  const entries: MenuEntry[] = [];
  const onUpdate = handlers.onUpdate;
  if (offers.update && onUpdate !== undefined) entries.push({ key: "update", label: UPDATE_LABEL, ariaLabel: "Update: " + title, onSelect: onUpdate });
  const editHint = options?.editHint;
  if (!offers.edit && typeof editHint === "string" && editHint !== "") entries.push({ key: "edit", label: EDIT_LABEL, ariaLabel: "Edit task, not available: " + title + ". " + editHint, onSelect: () => undefined, disabled: true, hint: editHint });
  if (offers.edit) entries.push({ key: "edit", label: EDIT_LABEL, ariaLabel: "Edit: " + title, onSelect: handlers.onEdit });
  if (offers.handoff) entries.push({ key: "handoff", label: HANDOFF_LABEL, ariaLabel: "Hand off: " + title, onSelect: handlers.onHandoff });
  if (offers.history) entries.push({ key: "history", label: HISTORY_LABEL, ariaLabel: "History: " + title, onSelect: handlers.onHistory });
  return entries;
}

/** What a Hand off does, in one sentence, for the menu hint and tests (the dialog says the same words). */
export const HANDOFF_EFFECT = "Handoff completes this task and creates another. It is not reassignment.";

